import { zodResolver } from '@hookform/resolvers/zod'
import { CaretLeft } from '@phosphor-icons/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Link, useParams } from 'react-router-dom'
import { DeviceFrameset } from 'react-device-frameset'
import 'react-device-frameset/styles/marvel-devices.min.css'
import { useRecoilValue } from 'recoil'
import { z } from 'zod'
import Listener from '@alphabait/ui/form/listener'
import { Button } from '@/components/button'
import CustomFileUpload from '@/components/form/custom-file-upload'
import CustomInput from '@/components/form/custom-input'
import { CustomSwitchComponent } from '@/components/form/custom-switch'
import { useCustomerClient } from '@/lib/api'
import { tokenState } from '@/store/atoms/token'
import { uploadFileGeneric } from '@/utils/file-upload-helper'
import { displayS3File, sameOriginS3File } from '@/utils/s3'
import BrandLayoutWrapper from '../../../../end-users/src/components/brand-layout-wrapper'
import { BrandProvider } from '../../../../end-users/src/contexts/brand-context'
import ChooseIncentive from '../../../../end-users/src/components/pages/brand/default/pages/choose-incentive'
import Expired from '../../../../end-users/src/components/pages/brand/default/pages/expired'
import HappinessCheck from '../../../../end-users/src/components/pages/brand/default/pages/happiness-check'
import ECHome from '../../../../end-users/src/components/pages/brand/default/pages/home'
import Invite from '../../../../end-users/src/components/pages/brand/default/pages/invite'
import Questionnaire from '../../../../end-users/src/components/pages/brand/default/pages/questionnaire'
import Success from '../../../../end-users/src/components/pages/brand/default/pages/success'
import type {
  EndUserCampaign,
  PartialEndUser,
} from '../../../../end-users/src/lib/api/types'
import {
  DEFAULT_END_USER_THEME,
  deriveColorScheme,
  END_USER_THEME_COLOR_ROLES,
  readTtfFontInfo,
  UpdateEndUserThemeBodySchema,
  type EndUserThemeColorRole,
  type ResolvedEndUserTheme,
} from '../../../../server/src/customer/end-user-theme.schema'
import { Brand } from '../../../../server/src/user/user.contract'
import { getEndUserPreviewData } from '../../../../server/src/utils'

type FormValues = z.infer<typeof UpdateEndUserThemeBodySchema>
type AssetType = 'font'
type PendingFont = { file: File; weight: string }

const COLOR_LABELS: Record<EndUserThemeColorRole, string> = {
  colorBackground: 'Background',
  colorBackgroundLight: 'Background (cards)',
  colorPrimary: 'Primary',
  colorPrimaryForeground: 'Text on primary',
  colorBaseButton: 'Button',
  colorSecondary: 'Secondary',
  colorForegroundLight: 'Default text, dropdowns and calendar',
  colorSlate50: 'Main text',
  colorBaseInput: 'Input border',
  colorBorder: 'Border',
  colorMutedForeground: 'Muted text',
  colorDestructive: 'Error',
}

const FONT_WEIGHTS = ['300', '400', '500', '600', '700']

const WEIGHT_NAMES: Record<string, string> = {
  '300': 'Light',
  '400': 'Regular',
  '500': 'Medium',
  '600': 'SemiBold',
  '700': 'Bold',
}

const weightLabel = (weight: string) =>
  `${WEIGHT_NAMES[weight] ?? 'Weight'} (${weight})`

// Stored keys are public/<customer>/end-user-theme/<uuid>-<file name>; show the file name.
const uploadedFileName = (key: string) =>
  (key.split('/').pop() ?? key).replace(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/,
    '',
  )

const familyNameHint = (usedBy: string) =>
  `Filled in from the font file: it is the font's own name, and ${usedBy} find the font by it. You do not need to type it; keep it as it is.`

// The theme-asset endpoint answers { key }, not the bare id the shared upload stores.
const themeAssetKey = (response: string) => JSON.parse(response).key as string

// Every screen the flow has, minus not-exists, which shows no theme of its own.
const PREVIEW_PAGES = [
  'home',
  'choose-incentive',
  'questionnaire',
  'happiness-check',
  'invite',
  'success',
  'expired',
] as const

type PreviewPage = (typeof PREVIEW_PAGES)[number]

// A #fff-style value is a zod regex failure; CustomInput shows t(`validation_messages.${message}`).
const hexErrorMap: z.ZodErrorMap = (issue, ctx) =>
  issue.code === z.ZodIssueCode.invalid_string
    ? { message: 'hex_color' }
    : { message: ctx.defaultError }

export default function EndUserThemeEditorPage() {
  const { customerId } = useParams<{ customerId: string }>()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const customerClient = useCustomerClient()
  const token = useRecoilValue(tokenState)?.__raw || ''

  const [pendingFonts, setPendingFonts] = useState<PendingFont[]>([])
  const [pendingHeadingFonts, setPendingHeadingFonts] = useState<PendingFont[]>(
    [],
  )
  const [saveMessage, setSaveMessage] = useState('')
  const [previewPage, setPreviewPage] = useState<PreviewPage>('home')

  const form = useForm<FormValues>({
    resolver: zodResolver(UpdateEndUserThemeBodySchema, {
      errorMap: hexErrorMap,
    }),
    defaultValues: {
      ...DEFAULT_END_USER_THEME,
      fontFiles: [],
      logoKey: null,
      heroImageKey: null,
      headingFontFamily: null,
      headingFontFiles: [],
      colorHeading: null,
      colorGreeting: null,
      headingUppercase: true,
    },
  })
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    register,
    getValues,
    formState,
  } = form

  const { data: saved, isLoading } = useQuery({
    queryKey: ['endUserTheme', customerId],
    queryFn: async () => {
      const response = await customerClient.getEndUserTheme.query({
        params: { customerId: customerId! },
      })
      return response.status === 200 ? response.body : null
    },
    enabled: !!customerId,
  })

  // reason: the form is filled from the API answer, both on load and right after a
  // save. Relying on the refetch alone fails: react-query keeps the same object when
  // the data did not change, so no re-render happens and a cleared key would stay cleared.
  const fillForm = useCallback(
    (theme: ResolvedEndUserTheme) => {
      // The API answers with URLs; the form stores keys, so strip the public prefix back off.
      const keyOf = (url: string | null) =>
        url ? url.replace(/^.*\/s3-files\//, '') : null
      reset({
        ...Object.fromEntries(
          END_USER_THEME_COLOR_ROLES.map((role) => [role, theme[role]]),
        ),
        fontFamily: theme.fontFamily,
        fontFiles: theme.fontFiles.map(({ key, weight }) => ({ key, weight })),
        logoKey: keyOf(theme.logoUrl),
        heroImageKey: keyOf(theme.heroImageUrl),
        headingFontFamily: theme.headingFontFamily,
        headingFontFiles: theme.headingFontFiles.map(({ key, weight }) => ({
          key,
          weight,
        })),
        colorHeading: theme.colorHeading,
        colorGreeting: theme.colorGreeting,
        headingUppercase: theme.headingUppercase,
      })
    },
    [reset],
  )

  useEffect(() => {
    if (saved) fillForm(saved)
  }, [saved, fillForm])

  const updateMutation = useMutation({
    mutationFn: async (body: FormValues) => {
      const response = await customerClient.updateEndUserTheme.mutate({
        params: { customerId: customerId! },
        body,
      })
      if (response.status !== 200) throw new Error('Save failed')
      return response.body
    },
  })

  const uploadAsset = async (
    file: File,
    assetType: AssetType,
  ): Promise<string> => {
    const text = await uploadFileGeneric({
      file,
      uploadUrl: '/upload/customer-theme-asset',
      uploadBody: { customerId: customerId!, assetType, theme: 'end-user' },
      token,
    })
    return JSON.parse(text).key as string
  }

  const onSubmit = async (values: FormValues) => {
    setSaveMessage('')
    // reason: without a family name the uploaded files are saved but never used.
    const unnamed = [
      [values.fontFamily, values.fontFiles, pendingFonts, 'Typeface'],
      [
        values.headingFontFamily,
        values.headingFontFiles,
        pendingHeadingFonts,
        'Home screen heading',
      ],
    ] as const
    for (const [family, saved, pending, section] of unnamed) {
      if (!family?.trim() && (saved?.length ?? 0) + pending.length > 0) {
        setSaveMessage(`${section}: add the font family name for its files`)
        return
      }
    }
    try {
      const upload = (pending: PendingFont[]) =>
        Promise.all(
          pending.map(async ({ file, weight }) => ({
            key: await uploadAsset(file, 'font'),
            weight,
          })),
        )
      const [uploadedFonts, uploadedHeadingFonts] = await Promise.all([
        upload(pendingFonts),
        upload(pendingHeadingFonts),
      ])
      const savedTheme = await updateMutation.mutateAsync({
        ...values,
        fontFiles: [...(values.fontFiles ?? []), ...uploadedFonts],
        headingFontFiles: [
          ...(values.headingFontFiles ?? []),
          ...uploadedHeadingFonts,
        ],
      })
      fillForm(savedTheme)
      queryClient.invalidateQueries({ queryKey: ['endUserTheme', customerId] })
      setPendingFonts([])
      setPendingHeadingFonts([])
      setSaveMessage('Theme saved')
    } catch (error) {
      setSaveMessage(error instanceof Error ? error.message : 'Save failed')
    }
  }

  const values = watch()
  const previewTheme = useMemo<ResolvedEndUserTheme>(() => {
    const withUrls = (
      saved: { key: string; weight: string }[] | null | undefined,
      pending: PendingFont[],
    ) => [
      ...(saved ?? []).map((f) => ({ ...f, url: displayS3File(f.key) })),
      ...pending.map((f) => ({
        key: f.file.name,
        weight: f.weight,
        url: URL.createObjectURL(f.file),
      })),
    ]
    const isHex = (value?: string | null) =>
      /^#[0-9A-Fa-f]{6}$/.test(value ?? '')
    const colours = Object.fromEntries(
      END_USER_THEME_COLOR_ROLES.map((role) => [
        role,
        isHex(values[role]) ? values[role]! : DEFAULT_END_USER_THEME[role],
      ]),
    ) as Record<EndUserThemeColorRole, string>
    return {
      ...colours,
      colorScheme: deriveColorScheme(colours.colorBackground),
      fontFamily: values.fontFamily || DEFAULT_END_USER_THEME.fontFamily,
      fontFiles: withUrls(values.fontFiles, pendingFonts),
      logoUrl: values.logoKey ? displayS3File(values.logoKey) : null,
      heroImageUrl: values.heroImageKey
        ? displayS3File(values.heroImageKey)
        : null,
      headingFontFamily: values.headingFontFamily || null,
      headingFontFiles: withUrls(values.headingFontFiles, pendingHeadingFonts),
      colorHeading: isHex(values.colorHeading) ? values.colorHeading! : null,
      colorGreeting: isHex(values.colorGreeting) ? values.colorGreeting! : null,
      headingUppercase: values.headingUppercase ?? true,
    }
  }, [values, pendingFonts, pendingHeadingFonts])

  const previewCampaign = useMemo(
    () => previewCampaignFor(customerId ?? 'preview', previewTheme),
    [customerId, previewTheme],
  )
  const previewEndUser = {
    ...getEndUserPreviewData(false),
    campaignId: previewCampaign.id,
    referral: false,
    expired: false,
    flowStatus: {
      hasCompletedQuestionnaire: false,
      hasCompletedHappinessCheck: false,
    },
  }

  const listeners = useMemo(() => new Listener(), [])
  const formProps = {
    register,
    control,
    errors: formState.errors,
    listeners,
    formValues: values,
    setValue,
    getValues,
  }

  if (isLoading) return <div className="p-6">Loading...</div>

  return (
    <div className="py-6 px-4">
      <Link
        to="/admin/customers"
        className="inline-flex items-center gap-2 h-8 text-sm text-foreground rounded-full hover:bg-gray-800/5 mb-2"
      >
        <CaretLeft weight="bold" className="w-4 h-4" />
        Customers
      </Link>

      <div className="-mx-4 mb-8 border-b border-base-input px-4 pb-4 pt-2">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="!text-2xl !font-semibold leading-7 text-foreground !mb-0">
              End-user theme
            </h1>
            <p className="text-sm text-muted-foreground">
              Colours, typeface and hero photo for this customer's end-user
              screens, and the postcard font.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {saveMessage && (
              <span
                className={`text-sm ${
                  saveMessage.includes('saved')
                    ? 'text-green-700'
                    : 'text-destructive'
                }`}
              >
                {saveMessage}
              </span>
            )}
            <Button
              type="button"
              primary
              size="small"
              label={updateMutation.isPending ? 'Saving...' : 'Save'}
              disabled={updateMutation.isPending}
              onClick={handleSubmit(onSubmit, () =>
                setSaveMessage('Please fix the highlighted fields'),
              )}
              className="bg-accent rounded-full"
            />
          </div>
        </div>
      </div>

      <div className="flex items-start gap-12">
        <div className="w-1/2 space-y-8 pb-16">
          <section>
            <SectionHeader title="Colours" usedOn="every end-user screen." />
            <div className="grid grid-cols-2 gap-4">
              {END_USER_THEME_COLOR_ROLES.map((role) => (
                <div key={role} className="flex flex-col gap-1">
                  <label className="text-xs text-foreground">
                    {COLOR_LABELS[role]}
                  </label>
                  <div className="flex items-center gap-2 [&>div:first-child]:w-10 [&>div:first-child_span]:hidden [&>div:last-child]:flex-1">
                    <CustomInput
                      field={{
                        name: role,
                        type: 'input',
                        uiOptions: { inputType: 'color', label: '' },
                      }}
                      formProps={formProps}
                    />
                    <CustomInput
                      field={{
                        name: role,
                        type: 'input',
                        uiOptions: {
                          inputType: 'text',
                          placeholder: '#rrggbb',
                          label: '',
                          testId: role,
                        },
                      }}
                      formProps={formProps}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <SectionHeader
              title="Typeface"
              usedOn="every end-user screen, the postcard text editor and the printed postcard."
            />
            <label className="text-xs text-foreground">Font family name</label>
            <CustomInput
              field={{
                name: 'fontFamily',
                type: 'input',
                uiOptions: {
                  inputType: 'text',
                  label: '',
                  testId: 'fontFamily',
                },
              }}
              formProps={formProps}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {familyNameHint('the screens, the postcard and the text editor')}
            </p>
            <FontFiles
              files={values.fontFiles ?? []}
              onFilesChange={(files) => setValue('fontFiles', files)}
              pending={pendingFonts}
              onPendingChange={setPendingFonts}
              family={values.fontFamily}
              onFamilyRead={(family) => setValue('fontFamily', family)}
              testId="font"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Regular (400) is required. Add Bold (700) for bold text on the
              postcard.
            </p>
          </section>

          {/* reason: an optional layer over the typeface and colours above; boxed so it
              reads as separate, not as part of the main typeface. */}
          <section className="!my-12 rounded-xl border border-base-input bg-gray-50 p-5">
            <SectionHeader
              title="Home screen heading (optional)"
              usedOn="the greeting and the headline of the home screen only. Empty fields keep the typeface and text colours above."
            />
            <label className="text-xs text-foreground">
              Heading font family name
            </label>
            <CustomInput
              field={{
                name: 'headingFontFamily',
                type: 'input',
                uiOptions: {
                  inputType: 'text',
                  label: '',
                  testId: 'headingFontFamily',
                },
              }}
              formProps={formProps}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {familyNameHint('the home screen')}
            </p>
            <FontFiles
              files={values.headingFontFiles ?? []}
              onFilesChange={(files) => setValue('headingFontFiles', files)}
              pending={pendingHeadingFonts}
              onPendingChange={setPendingHeadingFonts}
              family={values.headingFontFamily}
              onFamilyRead={(family) => setValue('headingFontFamily', family)}
              testId="headingFont"
            />
            <p className="text-xs text-muted-foreground mt-2">
              The headline uses the heaviest weight uploaded here.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {(
                [
                  ['colorGreeting', 'Greeting colour (optional)'],
                  ['colorHeading', 'Heading colour (optional)'],
                ] as const
              ).map(([name, label]) => (
                <div key={name} className="flex flex-col gap-1">
                  <label className="text-xs text-foreground">{label}</label>
                  <div className="flex items-center gap-2 [&>div:first-child]:w-10 [&>div:first-child_span]:hidden [&>div:last-child]:flex-1">
                    <CustomInput
                      field={{
                        name,
                        type: 'input',
                        uiOptions: { inputType: 'color', label: '' },
                      }}
                      formProps={formProps}
                    />
                    <CustomInput
                      field={{
                        name,
                        type: 'input',
                        uiOptions: {
                          inputType: 'text',
                          placeholder: 'empty: text colour',
                          label: '',
                          testId: name,
                        },
                      }}
                      formProps={formProps}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div className="w-12">
                <CustomSwitchComponent
                  name="headingUppercase"
                  control={control}
                  defaultValue
                />
              </div>
              <label className="text-xs text-foreground">
                Headline in capitals (home with hero photo)
              </label>
            </div>
          </section>

          <section>
            <SectionHeader
              title="Logo and hero photo"
              usedOn="the screen logo on every end-user screen; the hero photo on the home screen only."
            />
            <div className="mb-6">
              <label className="text-xs text-foreground block mb-1">
                Screen logo (optional)
              </label>
              <CustomFileUpload
                name="logoKey"
                control={control}
                uploadUrl="/upload/customer-theme-asset"
                uploadBody={{
                  customerId: customerId!,
                  assetType: 'logo',
                  theme: 'end-user',
                }}
                allowedFileTypes={['svg', 'png']}
                parseResponse={themeAssetKey}
                onClear={() => setValue('logoKey', null)}
                testId="logoFile"
                hint="Only for the end-user screens, when they need a different logo than the white email and postcard, for example on a dark background. Empty: the screens show the logo uploaded in compose creative. SVG, or a PNG with a transparent background at least 560 px wide. Shown at most 280 x 80 px, never stretched."
              />
            </div>
            <div className="mb-4">
              <label className="text-xs text-foreground block mb-1">
                Hero photo (optional)
              </label>
              <CustomFileUpload
                name="heroImageKey"
                control={control}
                uploadUrl="/upload/customer-theme-asset"
                uploadBody={{
                  customerId: customerId!,
                  assetType: 'hero',
                  theme: 'end-user',
                }}
                allowedFileTypes={['jpg', 'png', 'webp']}
                parseResponse={themeAssetKey}
                onClear={() => setValue('heroImageKey', null)}
                testId="heroFile"
                hint="Portrait, at least 1000 x 1500 px, with no logo in the photo. With a hero, the home screen shows it on the top two thirds."
              />
            </div>
          </section>
        </div>

        <div className="w-1/2">
          <div className="mb-3">
            <h2 className="text-lg font-semibold">Preview</h2>
            <p className="text-xs text-muted-foreground">
              Every screen of the flow, on a sample campaign.
            </p>
          </div>
          <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-base-input bg-gray-800/5 p-1">
            {PREVIEW_PAGES.map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setPreviewPage(page)}
                className={`h-8 rounded-lg px-3 text-xs transition-colors ${
                  previewPage === page
                    ? 'bg-primary font-medium text-white shadow-sm'
                    : 'text-muted-foreground hover:bg-white'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          <div className="flex justify-center">
            <DeviceFrameset device="iPhone X" color="gold" landscape={false}>
              <div
                className="flex flex-col overflow-y-auto overflow-x-hidden h-full w-full"
                style={{ scrollbarWidth: 'none' }}
              >
                <div className="pointer-events-none flex h-full min-h-0 w-full [container-type:size] [--sh:1cqh]">
                  <BrandProvider
                    brand={Brand.default}
                    endUserTheme={previewTheme}
                  >
                    <BrandLayoutWrapper brand={Brand.default}>
                      <PreviewScreen
                        page={previewPage}
                        campaign={previewCampaign}
                        endUser={previewEndUser}
                        t={t}
                      />
                    </BrandLayoutWrapper>
                  </BrandProvider>
                </div>
              </div>
            </DeviceFrameset>
          </div>
        </div>
      </div>
    </div>
  )
}

// The flow's screens, each with the props it needs and every action a no-op: the
// preview is there to be looked at, not walked through.
function PreviewScreen({
  page,
  campaign,
  endUser,
  t,
}: {
  page: PreviewPage
  campaign: EndUserCampaign
  endUser: PartialEndUser
  t: TFunction<'translation', undefined>
}) {
  const shared = {
    campaign,
    endUser,
    t,
    pathname: '/campaigns/preview',
    navigate: () => undefined,
  }
  const noop = () => undefined

  switch (page) {
    case 'home':
      return <ECHome {...shared} id={campaign.id} />
    case 'choose-incentive':
      return <ChooseIncentive {...shared} onSubmit={noop} setError={noop} />
    case 'questionnaire':
      return <Questionnaire {...shared} onSubmit={noop} />
    case 'happiness-check':
      return <HappinessCheck {...shared} onSubmit={noop} setError={noop} />
    case 'invite':
      return <Invite {...shared} id={campaign.id} integrations={[]} />
    case 'success':
      return <Success {...shared} />
    case 'expired':
      return <Expired {...shared} />
  }
}

// One field per shape the questionnaire can take, so the preview shows what the
// theme does to a label, a border, a dropdown, a date and a selected radio.
const PREVIEW_QUESTIONNAIRE = [
  {
    id: 'preview-name',
    name: 'preview-name',
    type: 'input',
    uiOptions: { label: 'Your name', inputType: 'text' },
  },
  {
    id: 'preview-email',
    name: 'preview-email',
    type: 'custom',
    customControlType: 'email',
    uiOptions: { label: 'Email address', inputType: 'email' },
  },
  {
    id: 'preview-select',
    name: 'preview-select',
    type: 'custom',
    customControlType: 'shadcn-select',
    uiOptions: { label: 'How did you hear about us?', inputType: 'select' },
    options: [
      { label: 'A friend', value: 'friend' },
      { label: 'Online', value: 'online' },
    ],
  },
  {
    id: 'preview-date',
    name: 'preview-date',
    type: 'custom',
    customControlType: 'dateInput',
    uiOptions: { label: 'Date of birth' },
  },
  {
    id: 'preview-radio',
    name: 'preview-radio',
    type: 'custom',
    customControlType: 'radioGroup',
    uiOptions: { label: 'Which do you prefer?' },
    options: [
      { label: 'The first one', value: 'first' },
      { label: 'The second one', value: 'second' },
    ],
  },
  {
    id: 'preview-toggle',
    name: 'preview-toggle',
    type: 'custom',
    customControlType: 'switch',
    uiOptions: { label: 'Keep me posted' },
  },
]

// The same five faces the admin's happiness-check step offers.
const PREVIEW_HAPPINESS_CHECK = [
  {
    name: 'happinessCheck',
    type: 'custom',
    customControlType: 'radioGroupHappiness',
    iconTheme: 'faces',
    options: [
      { name: 'Really negative', value: '1', Icon: 'bad' },
      { name: 'Negative', value: '2', Icon: 'belowAverage' },
      { name: 'Alright', value: '3', Icon: 'average' },
      { name: 'Positive', value: '4', Icon: 'aboveAverage' },
      { name: 'Really positive', value: '5', Icon: 'good' },
    ],
  },
]

// The shape a real incentive photo has: 1200 x 600, the ratio the upload asks for
// and the one the card's 2:1 frame expects. Drawn inline so the preview makes no
// network call and the card is not judged on a placeholder.
const PREVIEW_INCENTIVE_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 600'%3E%3Crect width='1200' height='600' fill='%23d4d4d4'/%3E%3Ccircle cx='430' cy='330' r='130' fill='%23a3a3a3'/%3E%3Crect x='640' y='240' width='320' height='180' rx='20' fill='%23a3a3a3'/%3E%3C/svg%3E"

// A dashed 450 x 200 box, the compose creative logo size, marking the logo slot.
const PREVIEW_CAMPAIGN_LOGO =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 450 200'%3E%3Crect x='4' y='4' width='442' height='192' rx='12' fill='none' stroke='%23a3a3a3' stroke-width='6' stroke-dasharray='18 12'/%3E%3Ctext x='225' y='112' font-family='sans-serif' font-size='30' fill='%23a3a3a3' text-anchor='middle'%3ECompose creative logo%3C/text%3E%3C/svg%3E"

// Enough of a campaign for the default screens to render.
function previewCampaignFor(
  customerId: string,
  endUserTheme: ResolvedEndUserTheme,
): EndUserCampaign {
  const incentive = (id: string, name: string) => ({
    id,
    referral: false,
    incentiveItem: {
      id,
      name,
      description: 'Sample incentive',
      isAvailable: true,
      picture: PREVIEW_INCENTIVE_IMAGE,
      skuCode: null,
    },
  })
  return {
    id: 'preview',
    name: 'Preview',
    companyName: 'Preview',
    // Stands in for the compose creative logo, so the preview shows where it goes.
    logo: PREVIEW_CAMPAIGN_LOGO,
    salutation: null,
    details: '',
    referralCampaign: true,
    questionnaire: { form: PREVIEW_QUESTIONNAIRE },
    referralQuestionnaire: { form: PREVIEW_QUESTIONNAIRE },
    happinessCheck: PREVIEW_HAPPINESS_CHECK,
    endDate: null,
    mainLandingText: null,
    referralLandingText: null,
    postcardTemplate: null,
    campaignIncentives: [
      incentive('a', 'Gift card'),
      incentive('b', 'Voucher'),
    ],
    campaignCategory: 'generic',
    ecFlowTexts: null,
    languages: null,
    customer: { id: customerId, brand: Brand.default, endUserTheme },
    isTest: true,
    endUserUrl: '',
  } as unknown as EndUserCampaign
}

function SectionHeader({ title, usedOn }: { title: string; usedOn: string }) {
  return (
    <div className="mb-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="text-xs text-muted-foreground">Used on: {usedOn}</p>
    </div>
  )
}

// One TTF per weight. A picked file is read first: a variable or non-TTF file is
// refused, and the family name inside it fills the family field.
function FontFiles({
  files,
  onFilesChange,
  pending,
  onPendingChange,
  family,
  onFamilyRead,
  testId,
}: {
  files: { key: string; weight: string }[]
  onFilesChange: (files: { key: string; weight: string }[]) => void
  pending: PendingFont[]
  onPendingChange: (pending: PendingFont[]) => void
  family?: string | null
  onFamilyRead: (family: string) => void
  testId: string
}) {
  const [newWeight, setNewWeight] = useState('400')
  const [fileError, setFileError] = useState('')
  const [variableKeys, setVariableKeys] = useState<string[]>([])

  // reason: files saved before the name was read from the file can carry no name, or
  // be a variable font. Read them once: fill an empty name, flag a variable file.
  const savedKeys = files.map((file) => file.key).join('|')
  useEffect(() => {
    let cancelled = false
    Promise.all(
      files.map(async (file) => ({
        key: file.key,
        info: readTtfFontInfo(
          await (await fetch(sameOriginS3File(file.key))).arrayBuffer(),
        ),
      })),
    )
      .then((read) => {
        if (cancelled) return
        setVariableKeys(read.filter((r) => r.info?.variable).map((r) => r.key))
        const name = read.find((r) => r.info?.family)?.info?.family
        if (!family?.trim() && name) onFamilyRead(name)
      })
      .catch((error) =>
        console.error('Saved font files could not be read', error),
      )
    return () => {
      cancelled = true
    }
    // Only when the saved files change; family and the callback are read, not watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKeys])

  const addFile = async (file: File) => {
    setFileError('')
    const info = readTtfFontInfo(await file.arrayBuffer())
    if (!info) {
      setFileError(`${file.name} is not a TrueType font (.ttf).`)
      return
    }
    if (info.variable) {
      setFileError(
        `${file.name} is a variable font. Upload one fixed weight instead, for example from the static/ folder of a Google Fonts download.`,
      )
      return
    }
    if (info.family) onFamilyRead(info.family)
    onPendingChange([...pending, { file, weight: newWeight }])
  }

  const rows = [
    ...files.map((file, index) => ({
      key: file.key,
      name: uploadedFileName(file.key),
      weight: file.weight,
      note: variableKeys.includes(file.key)
        ? 'variable font: remove it and add a fixed weight'
        : '',
      warn: variableKeys.includes(file.key),
      remove: () => onFilesChange(files.filter((_, i) => i !== index)),
    })),
    ...pending.map((item, index) => ({
      key: item.file.name,
      name: item.file.name,
      weight: item.weight,
      note: 'not saved yet',
      warn: false,
      remove: () => onPendingChange(pending.filter((_, i) => i !== index)),
    })),
  ]

  return (
    <div className="mt-3">
      {rows.length > 0 && (
        <ul className="text-sm divide-y divide-base-input border border-base-input rounded-lg mb-3">
          {rows.map((row) => (
            <li
              key={row.key}
              className="flex items-center justify-between gap-3 px-3 py-2"
            >
              <span className="truncate">{row.name}</span>
              <span className="flex items-center gap-3 shrink-0">
                <span className="text-muted-foreground">
                  {weightLabel(row.weight)}
                </span>
                {row.note && (
                  <span
                    className={
                      row.warn ? 'text-destructive' : 'text-muted-foreground'
                    }
                  >
                    {row.note}
                  </span>
                )}
                <button
                  type="button"
                  className="text-destructive text-xs"
                  onClick={row.remove}
                >
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <select
          className="h-9 border border-base-input rounded-lg px-2 text-sm"
          value={newWeight}
          onChange={(e) => setNewWeight(e.target.value)}
          data-testid={`${testId}Weight`}
        >
          {FONT_WEIGHTS.map((w) => (
            <option key={w} value={w}>
              {weightLabel(w)}
            </option>
          ))}
        </select>
        <label className="h-9 inline-flex items-center px-3 border border-base-input rounded-lg text-sm cursor-pointer hover:bg-gray-800/5">
          + Add font file (.ttf)
          <input
            type="file"
            accept=".ttf,font/ttf"
            className="hidden"
            data-testid={`${testId}File`}
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (file) void addFile(file)
            }}
          />
        </label>
      </div>
      {fileError && (
        <p className="text-xs text-destructive mt-2">{fileError}</p>
      )}
    </div>
  )
}
